/**
 * Formatação numérica pt-BR (vírgula como separador decimal).
 * Usa instâncias únicas de Intl.NumberFormat para desempenho.
 */

const decimalFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Sem casas fixas — só mostra decimais quando o valor realmente tem (produto com
// casasDecimaisQuantidade=0 exibe "5", não "5,00"; um produto fracionado continua exibindo
// "1,25"). Usado para quantidade de estoque, nunca para valores monetários.
const quantidadeFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formata valor numérico com vírgula e casas decimais (padrão 2).
 * @param {number|string|null|undefined} value
 * @param {number} decimals
 * @returns {string} Ex: "1.234,56" ou "-" se inválido
 */
export function formatDecimalPtBr(value, decimals = 2) {
  if (value === null || value === undefined) return '-';
  const n = Number(value);
  if (isNaN(n)) return '-';
  if (decimals !== 2) {
    return n.toLocaleString('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return decimalFormatter.format(n);
}

/**
 * Formata quantidade de estoque (pt-BR) sem casas decimais fixas — até 3 casas, só quando
 * o valor as tem. Ex.: 5 -> "5", 1.25 -> "1,25". Ver models/.../Produto.js:casasDecimaisQuantidade.
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function formatQuantidadeEstoque(value) {
  if (value === null || value === undefined) return '-';
  const n = Number(value);
  if (isNaN(n)) return '-';
  return quantidadeFormatter.format(n);
}

/**
 * Formata valor como moeda R$ com vírgula decimal (pt-BR).
 * @param {number|string|null|undefined} value
 * @returns {string} Ex: "R$ 1.234,56" ou "-" se inválido
 */
export function formatCurrencyPtBr(value) {
  if (value === null || value === undefined) return '-';
  const n = Number(value);
  if (isNaN(n)) return '-';
  return currencyFormatter.format(n);
}

/**
 * Moeda BRL; valores inválidos tratados como 0 (útil em grades onde zero deve ser "R$ 0,00").
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function formatCurrencyPtBrZero(value) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return currencyFormatter.format(safe);
}

/**
 * Formata valor para exibição em input de moeda (sempre BRL).
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function formatCurrencyInputPtBr(value) {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return currencyFormatter.format(n);
}

/**
 * Converte texto digitado em input moeda para número em reais.
 * Ex.: "12345" -> 123.45
 * @param {string|null|undefined} rawValue
 * @returns {number|string}
 */
export function parseCurrencyInputPtBr(rawValue) {
  const digits = String(rawValue ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits) / 100;
}

/**
 * Converte string pt-BR (vírgula decimal) para número.
 * @param {string|number|null|undefined} str
 * @returns {number}
 */
export function parseDecimalPtBr(str) {
  if (str === null || str === undefined) return 0;
  if (typeof str === 'number') return isNaN(str) ? 0 : str;
  const cleaned = String(str).replace(',', '.').replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}
